import { useCallback, useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "./ui/button";

const DISMISS_KEY = "castfolio_pwa_install_dismissed";
const DISMISS_DAYS = 14; // don't show again for 14 days after dismissal

function isRecentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    const ageMs = Date.now() - ts;
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  // iOS Safari uses navigator.standalone
  const iosStandalone = typeof navigator !== "undefined" && navigator.standalone === true;
  return mq || iosStandalone;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (isRecentlyDismissed()) return;

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onAppInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch { /* noop */ }
    setDeferredPrompt(null);
    setVisible(false);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  };

  if (!visible || !deferredPrompt) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[60]
                 rounded-2xl bg-gradient-to-br from-zinc-900 to-zinc-900/95 border border-rose-500/25
                 shadow-[0_12px_40px_-8px_rgba(225,29,72,0.3)] backdrop-blur-xl p-4"
    >
      <button
        data-testid="pwa-install-dismiss"
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded-full text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/60"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center shadow-lg shadow-rose-500/20 flex-shrink-0">
          <Download className="w-5 h-5 text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-sm font-semibold text-zinc-100">Install Castfolio</p>
          <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">
            Get the native app feel — faster loads, home-screen icon, full-screen scanning.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button
          data-testid="pwa-install-confirm"
          onClick={handleInstall}
          size="sm"
          className="flex-1 bg-rose-600 hover:bg-rose-500 text-white border border-rose-500"
        >
          <Download className="w-3.5 h-3.5 mr-1" /> Install
        </Button>
        <Button
          onClick={handleDismiss}
          variant="outline"
          size="sm"
          className="bg-zinc-950 border-zinc-800 text-zinc-400 hover:bg-zinc-800"
        >
          Not now
        </Button>
      </div>
    </div>
  );
}
